import { useState, useEffect } from 'react';
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  IconButton,
  Modal,
  Box,
  CircularProgress,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { v4 as uuidv4 } from 'uuid';

import { ActionItem } from '@urbanmix-tech/shared-js';
import { uploadUnitAttachment } from '@/data';
import { useAuth } from '@/shared/context/AuthProvider';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { useUIContext, useLoading } from '@/shared/context/UIContextProvider';
import { useSignedUrls } from '@/shared/hooks/useSignedUrls';
import { FileUpload } from '@/components/shared/FileUploads/FileUpload';
import { SCOPE_CATEGORIES } from './ActionItemModal/ActionItemModal';
import { convertPseudoDateToRealDate } from '@/helpers/common';

const PRIORITIES = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

export interface TaskImage {
  id: string;
  url: string;
  note?: string;
  type?: 'before' | 'after';
}

export interface InspectResponse {
  images?: TaskImage[];
  assignedTo?: string;        // userId
  assignedToEmail?: string;   // email
  priority?: string;
  targetDate?: string | null;
  notes?: string;
  category?: string;          // passed through from existing response
}

interface InspectionScopeContentProps {
  actionItem: ActionItem;
  onChange?: (data: InspectResponse) => void;
}

export function InspectionScopeContent({
  actionItem,
  onChange,
}: InspectionScopeContentProps) {
  const { usersData } = useAuth();
  const { showAlert } = useAlert();
  const { openConfirm } = useUIContext();
  const { createLoading } = useLoading();

  const existingResponse = actionItem.response || {};

  // Form state - category is read-only from inspect app
  const [assignedTo, setAssignedTo] = useState(existingResponse.assignedTo || '');
  const [priority, setPriority] = useState(existingResponse.priority || 'medium');
  const [targetDate, setTargetDate] = useState<string>(
    existingResponse.targetDate ? new Date(existingResponse.targetDate).toISOString().split('T')[0] : ''
  );
  const [notes, setNotes] = useState(existingResponse.notes || '');
  const [images, setImages] = useState<TaskImage[]>(existingResponse.images || []);

  // UI state
  const [selectedImage, setSelectedImage] = useState<TaskImage | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const allUsers = Object.values(usersData);
  const signedUrls = useSignedUrls(images);

  // Report changes to parent
  useEffect(() => {
    if (onChange) {
      const selectedUser = assignedTo ? usersData[assignedTo] : null;
      onChange({
        images,
        assignedTo,
        assignedToEmail: selectedUser?.email || '',
        priority,
        targetDate: targetDate ? new Date(targetDate).toISOString() : null,
        notes,
        category: existingResponse.category,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, assignedTo, priority, targetDate, notes]);

  // Get category label from value
  const getCategoryLabel = (value: string) => {
    const cat = SCOPE_CATEGORIES.find((c) => c.value === value);
    return cat?.label || value || 'Not specified';
  };

  const handleDeleteImage = (imageId: string) => {
    openConfirm({
      title: 'Delete Image',
      description: 'Are you sure you want to delete this image?',
      onAction: (confirmed) => {
        if (confirmed) {
          setImages((prev) => prev.filter((img) => img.id !== imageId));
          if (selectedImage?.id === imageId) {
            setSelectedImage(null);
          }
        }
      },
    });
  };

  const handleUpload = async (file: File) => {
    const loader = createLoading();
    loader.startLoading({ title: 'Uploading image...' });

    try {
      const url = await uploadUnitAttachment(
        file,
        file.name,
        actionItem.projectId,
        actionItem.id
      );

      if (url) {
        const newImage: TaskImage = {
          id: uuidv4(),
          url,
          type: 'before',
        };
        setImages((prev) => [...prev, newImage]);
        setShowUpload(false);
        showAlert('Image uploaded successfully', 'success');
      } else {
        showAlert('Failed to upload image', 'error');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showAlert('Failed to upload image', 'error');
    } finally {
      loader.stopLoading();
    }
  };

  // Full-screen image overlay
  if (selectedImage) {
    const displayUrl = signedUrls[selectedImage.id] || selectedImage.url;
    return (
      <Modal open onClose={() => setSelectedImage(null)}>
        <Box
          className="absolute left-1/2 top-1/2 h-[90%] w-[90%] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-primary bg-black p-4"
          onClick={() => setSelectedImage(null)}
        >
          <IconButton
            onClick={() => setSelectedImage(null)}
            className="absolute right-4 top-4 z-10 text-white"
          >
            <ClearIcon fontSize="large" />
          </IconButton>
          <img
            src={displayUrl}
            alt=""
            className="h-full w-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Read-only Inspection Details */}
      {/* <div className="rounded-lg bg-gray-800/50 p-12">
        <Typography variant="subtitle2" className="mb-3 text-stone-300">
          Inspection Details
        </Typography>
        <Stack spacing={.5} className='grid grid-cols-2 cursor-default'>
          <Stack direction="row" alignItems="center" gap={1}>
            <CategoryIcon sx={{ fontSize: 18, color: 'gray' }} />
            <Typography variant="body2" className="text-stone-300">
              Category:
            </Typography>
            <Typography variant="body2" className="text-white">
              {getCategoryLabel(existingResponse.category)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'gray' }} />
            <Typography variant="body2" className="text-stone-300">
              Status:
            </Typography>
            <Typography variant="body2" className="capitalize text-white">
              {actionItem.status}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <CalendarTodayIcon sx={{ fontSize: 18, color: 'gray' }} />
            <Typography variant="body2" className="text-stone-300">
              Created:
            </Typography>
            <Typography variant="body2" className="text-white">
              {convertPseudoDateToRealDate(actionItem.createDate)?.toLocaleDateString() || '-'}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <WarningAmberIcon
              sx={{
                fontSize: 18,
                color: actionItem.severtity === 'urgent' ? 'orange' : 'gray',
              }}
            />
            <Typography variant="body2" className="text-stone-300">
              Severity:
            </Typography>
            <Typography variant="body2" className="capitalize text-white">
              {actionItem.severtity || 'Regular'}
            </Typography>
          </Stack>
        </Stack>
      </div> */}

      {/* Image Gallery Section */}
      <div className="rounded-lg bg-gray-800/50 p-12">
        {/* <Typography variant="subtitle2" className="mb-2 text-stone-300">
          Photos
        </Typography> */}

        {images.length > 0 && (
          <div className="mb-3 w-full flex flex-col gap-2">
            {images.map((img) => {
              const displayUrl = signedUrls[img.id] || '';
              return (
                <div key={img.id} className="relative w-full">
                  {displayUrl ? (
                    <img
                      src={displayUrl}
                      alt=""
                      className="w-full h-[200px] cursor-pointer rounded object-cover hover:opacity-80"
                      onClick={() => setSelectedImage(img)}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      className="flex w-full h-[100px] items-center justify-center rounded bg-gray-700"
                      style={{ height: '96px' }}
                    >
                      <CircularProgress size={20} />
                    </div>
                  )}
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    className="mt-1"
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteImage(img.id)}
                    >
                      <DeleteIcon className="text-red-400" fontSize="small" />
                    </IconButton>
                  </Stack>
                </div>
              );
            })}
          </div>
        )}

      </div>
      {showUpload ? (
        <div className="mb-3">
          <FileUpload onChangeFile={handleUpload} />
          <Button
            size="small"
            onClick={() => setShowUpload(false)}
            className="mt-2 text-gray-400"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileUploadIcon />}
          onClick={() => setShowUpload(true)}
          className="!border-gray-600 !text-gray-300"
        >
          {images.length > 0 ? 'Add More Photos' : 'Upload Photos'}
        </Button>
      )}

      <hr className="border-gray-700 pb-4" />

      {/* Scoping Form Section */}
      <Typography variant="subtitle2" className="!mt-4 text-stone-300">
        Scope Assignment
      </Typography>

      <FormControl fullWidth variant="outlined" size="small">
        <InputLabel>Assignee</InputLabel>
        <Select
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
          label="Assignee"
        >
          <MenuItem value="">
            <em>Unassigned</em>
          </MenuItem>
          {allUsers.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.fullName || user.email}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth variant="outlined" size="small">
        <InputLabel>Priority</InputLabel>
        <Select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          label="Priority"
        >
          {PRIORITIES.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Target Date"
        type="date"
        value={targetDate}
        onChange={(e) => setTargetDate(e.target.value)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Notes"
        multiline
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        size="small"
      />
    </Stack>
  );
}
